/* eslint-disable indent */
import { useState, useEffect } from 'react';
import api from '../utils/api';
import { ModelSection, ModelCard } from '../components/models/types';
import { ApiModelsResponse, ApiModel } from '../components/models/apiTypes';
import VideoImage from '../assets/Video.png';
import VideoImage1 from '../assets/Video2.png';
import VideoImage3 from '../assets/Video3.png';
import VideoImage4 from '../assets/Video5.png';
import VideoImage5 from '../assets/Video6.png';

/**
 * Hook to fetch image models data
 * TODO: Replace with actual API call when backend is ready
 * Example API integration:
 * 
 * const fetcher = async (url: string) => {
 *   const response = await api.get(url);
 *   return response.data;
 * };
 * 
 * const { data, error, isLoading } = useSWR('/api/image-models', fetcher);
 * return { sections: data, isLoading, error };
 */



// Helper function to pick a placeholder image deterministically based on ID
const getPlaceholderImage = (index: number) => {
  const images = [VideoImage, VideoImage1, VideoImage3, VideoImage4, VideoImage5];
  return images[index % images.length];
};

// Cache for models data to prevent refetching on every navigation
let cachedSections: ModelSection[] | null = null;
let isFetching = false;

export const useImageModels = () => {
  const [sections, setSections] = useState<ModelSection[]>(cachedSections || []);
  const [isLoading, setIsLoading] = useState(!cachedSections);

  useEffect(() => {
    // If we already have cached data, use it immediately (no refetch needed)
    if (cachedSections) {
      setSections(cachedSections);
      setIsLoading(false);
      return;
    }

    // Prevent multiple simultaneous fetches
    if (isFetching) return;
    
    // Simulate API call delay
    const fetchImageModels = async () => {
      isFetching = true;
      setIsLoading(true);

      try {
        const response = await api.get<ApiModelsResponse>('/image-generation/v1/models?offset=0&size=100');
        // console.log('API Response for models:', response.data);
        
        // List of specific titles to EXCLUDE completely
        const excludedTitles = [
            'Google - Nano Banana Pro Image to Image'
        ];

        if (response.data && response.data.models) {
          const apiModels = response.data.models;

          // Define sections arrays
          let textToImageModels: ModelCard[] = [];
          // textToImageLoraModels now populated from API
          let textToImageLoraModels: ModelCard[] = [];
          let imageEditingModels: ModelCard[] = [];

          let imageIndex = 0;

          // 1. Process API models for T2I, LoRA, and Editing
          // Show ALL models from API, grouped by modelType (no allowed list filtering)
          apiModels.forEach((model: ApiModel) => {
            // Skip excluded titles
            if (excludedTitles.includes(model.name)) {
              return;
            }

            const modelCard: ModelCard = {
              id: model.id,
              title: model.name,
              description: model.description,
              image: model.previewImageUrl || getPlaceholderImage(imageIndex++),
              selected: false,
              apiModel: model // Store full API model data
            };

            // Add to appropriate section based on modelType
            if (model.modelType === 'text-to-image') {
              textToImageModels.push(modelCard);
            } else if (model.modelType === 'text-to-image-lora') {
              textToImageLoraModels.push(modelCard);
            } else if (model.modelType === 'image-editing') {
              imageEditingModels.push(modelCard);
            }
          });

          // 2. Excluded titles are already filtered during processing above

          // 3. Sort by API position field (maintains backend order)
          const sortByPosition = (models: ModelCard[]) => {
            return models.sort((a, b) => {
              const positionA = a.apiModel?.position ?? Number.MAX_SAFE_INTEGER;
              const positionB = b.apiModel?.position ?? Number.MAX_SAFE_INTEGER;
              return positionA - positionB;
            });
          };

          textToImageModels = sortByPosition(textToImageModels);
          textToImageLoraModels = sortByPosition(textToImageLoraModels);
          imageEditingModels = sortByPosition(imageEditingModels);

          const newSections: ModelSection[] = [];


           // LoRA section is guaranteed by the hardcoded list (length > 0)
          if (textToImageLoraModels.length > 0) {
            newSections.push({
              title: 'Text to Image (With LoRA)',
              models: textToImageLoraModels
            });
          }

          if (textToImageModels.length > 0) {
            newSections.push({
              title: 'Text To Image',
              models: textToImageModels
            });
          }

         

          if (imageEditingModels.length > 0) {
            newSections.push({
              title: 'Image Editing',
              models: imageEditingModels
            });
          }

          // Cache the sections for future use
          cachedSections = newSections;
          setSections(newSections);

        } else {
            // Handle case where API call succeeds but returns no models
            cachedSections = [];
            setSections([]);
        }

      } catch (error) {
        console.error('Error fetching models:', error);
        // On error, show empty sections
        cachedSections = [];
        setSections([]);
      } finally {
        setIsLoading(false);
        isFetching = false;
      }
    };

    fetchImageModels();
  }, []);

  return { sections, isLoading };
};